import { useState } from 'react'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import { Button } from '@mui/material'
import { DateTime } from 'luxon'
import DashboardErrorView from '@pages/Dashboard/components/DashboardErrorView'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import { Org } from '@types'
import API from '@enums/api'
import useDataSource from '@hooks/useDataSource'
import CustomToolbar from '@components/CustomToolbar'

export default function ReviewOrgsPage(): JSX.Element {
    const [error, setError] = useState<string | null>(null)

    const { dataSource, exporting, exportAllRows, setFilterModel } =
        useDataSource({
            getRows: async ({ paginationModel, sortModel, filterModel }) => {
                const res = await API.overviews.organizations({
                    paginationModel,
                    sortModel,
                    filterModel,
                    approval: 'pending',
                })

                if (!res.ok) {
                    console.error(res)
                    setError(res.error.message)
                    throw new Error(res.error.message)
                }

                return {
                    rows: res.value.rows,
                    rowCount: res.value.total_rows,
                }
            },
        })

    const columns: GridColDef<Org>[] = [
        {
            field: 'name',
            headerName: 'Name',
            type: 'string',
            flex: 2.2,
        },
        {
            field: 'created_date',
            headerName: 'Created Date',
            type: 'dateTime',
            flex: 1.3,
            valueFormatter: (_value, { created_date }) =>
                DateTime.fromISO(created_date).toLocaleString(
                    DateTime.DATE_SHORT
                ),
        },
        {
            field: 'country',
            headerName: 'Country',
            type: 'string',
            flex: 1.2,
        },
        {
            field: 'currency',
            headerName: 'Currency',
            type: 'string',
        },
        {
            field: 'address',
            headerName: 'Address',
            type: 'string',
            flex: 2.6,
            valueGetter: (_value, row) => {
                const { address, city, state, country, postcode } = row
                return [address, city, state, country, postcode]
                    .filter(Boolean)
                    .join(', ')
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            type: 'actions',
            filterable: false,
            sortable: false,
            flex: 2,
            renderCell: ({ row }) => (
                <Button href={`/review-orgs/${row.id}`}>Review</Button>
            ),
        },
    ]

    if (error) {
        return <DashboardErrorView error={error} />
    }

    return (
        <DashboardLayout>
            <DataGridPro
                columns={columns}
                pageSizeOptions={[10, 20, 50, 100]}
                pagination
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                dataSource={dataSource}
                onFilterModelChange={(m) => setFilterModel(m)}
                disableVirtualization // Disabled in order to prevent row-ghosting
                showToolbar
                slots={{
                    toolbar: CustomToolbar,
                }}
                slotProps={{
                    toolbar: {
                        exporting,
                        exportAllRows,
                        exportFilename: 'pending-org-applications',
                    },
                }}
            />
        </DashboardLayout>
    )
}
